import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { connect } from 'redux-bundler-react';
import { mdiPlusBox, mdiCloseOctagon } from '@mdi/js';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@trussworks/react-uswds';
import _isEqual from 'lodash/isEqual';
import { toast } from 'react-toastify';

import Icon from '@components/icon/Icon';
import StepHeader from '@components/step-header/StepHeader';
import AddPropertyOwnerModal from '@forms/components/modals/AddPropertyOwnerModal';
import AgentsActionsTableCell from '@src/app-components/table/tableCellComponents/AgentsActionsTableCell';
import SelectInput from '@components/select/Select';
import FileInput from '@components/file-input/FileInput';
import statusModal from '@forms/components/modals/statusModal';
import LinkButton from '@components/link/linkButton';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import TanStackTableBasic from '@components/table/TanStackTable/TanStackTableBasic';
import Section from '@components/section/Section';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';

import RightOfEntryUpload from '@forms/components/Form/RightOfEntryUpload';
import { ContactTypes, ContactsFormNames, FileTypes, ResourceFiles, ErrorMessages } from '@src/utils/enums';
import { parseCsvWithXlsx, validateCsvHeaders } from '@src/utils/helpers';
import { tError, tSuccess } from '@src/utils/toast-helpers';
import { useRequest, useModal } from '@stores';
import { useResetDirtyFields } from '@hooks';

import {
  formatState,
  formatCountry,
  formatOptionString,
  salutations,
  phonetypes,
  validateCSV,
} from '@forms/input-forms/helpers';
import { CsvValidationModal } from '../../components/modals/CsvValidationModal';
import { useContactsTable } from '@src/stores/contactsTable.store';
import { handleFileProcessing } from '@src/utils/fileProcessing';

export const PreAppFormPropertyOwnersMetadata = {
  sectionName: 'Property Owners',
};

const PreAppFormPropertyOwners = connect(
  'doDownloadFile',
  'doDownloadPopulatedFile',
  'doClearContactType',
  'doUpdatePreAppRequest',
  'selectRequestAPIData',
  ({
    doDownloadFile,
    doDownloadPopulatedFile,
    doClearContactType,
    doUpdatePreAppRequest,
    requestAPIData,
    componentID,
    stepNo,
  }) => {
    const { openModal, openSecondaryModal } = useModal();
    const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched } = useRequest();
    const saveContactsTable = useContactsTable((state) => state.saveContactsTable);

    const [rowData, setRowData] = useState([]);
    const [tableIsDirty, setTableIsDirty] = useState(false);
    const [rowErrorIndices, setRowErrorIndices] = useState([]);
    const [stagedFile, setStagedFile] = useState([]);

    const propertyOwnerTableLength = rowData?.length;

    const prevRowDataRef = useRef([]);

    const schema = (propertyOwnerTableLength) =>
      yup.object().shape(
        {
          rightOfEntryFile: yup
            .string()
            .test('file-invalid-test', 'Right-of-Entry: File selected is invalid', function (value) {
              return value !== 'error';
            }),
          hasAddPropertyOwners: yup.string().required('Please select an option'),
          propertyOwnersTable: yup.boolean().when('hasAddPropertyOwners', {
            is: (val) => (val === 'true' || val === true) && propertyOwnerTableLength < 1,
            then: () => yup.boolean().required('Property Owners: At least one property owner is required'),
          }),
          ownerEntryComments: yup.string().nullable(),
        },
        [['rightOfEntryFile', 'rightOfEntryFile']]
      );

    const defaultValues = {
      hasAddPropertyOwners:
        requestAPIData?.request?.preApplications?.[0]?.hasAddPropertyOwners !== null
          ? requestAPIData?.request?.preApplications?.[0]?.hasAddPropertyOwners?.toString()
          : null,
      ownerEntryComments: requestAPIData?.request?.ownerEntryComments ?? '',
    };

    const methods = useForm({
      resolver: yupResolver(schema(propertyOwnerTableLength)),
      mode: 'onBlur',
      defaultValues: defaultValues,
    });
    const {
      formState: { errors, dirtyFields },
      watch,
      setError,
      trigger,
      reset,
    } = methods;
    const isDirty = Object.values(dirtyFields).length > 0;
    const errorCount = Object.values(errors)?.length;
    const isValid = errorCount === 0;
    const isTouched = isSectionTouched(stepNo);

    const hasAddPropertyOwners = watch('hasAddPropertyOwners');
    const columnHelper = createColumnHelper();

    const columnDefs = useMemo(
      () => [
        columnHelper.display({
          header: 'Actions',
          id: 'actions',
          cell: ({ row }) => (
            <AgentsActionsTableCell
              row={row}
              rowData={rowData}
              setRowData={setRowData}
              modalComponent={AddPropertyOwnerModal}
              isReadOnly={isReadOnly}
              contactType={ContactTypes.PropertyOwner}
            />
          ),
          size: 60,
          enableSorting: false,
          meta: {
            centerText: true,
          },
        }),
        columnHelper.accessor('firstName', {
          header: 'First Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
        }),
        columnHelper.accessor('middleName', {
          header: 'Middle Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('lastName', {
          header: 'Last Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
        }),
        columnHelper.accessor('address', {
          header: 'Address',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 500,
        }),
        columnHelper.accessor('city', {
          header: 'City',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
        }),
        columnHelper.accessor('state', {
          header: 'State',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 75,
        }),
        columnHelper.accessor('country', {
          header: 'Country',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 75,
        }),
      ],
      [columnHelper, rowData, isReadOnly]
    );

    useEffect(() => {
      const dirty = isDirty || tableIsDirty;
      updateSection(stepNo, { dirty: dirty, valid: isValid });
    }, [stepNo, isDirty, isValid, tableIsDirty, updateSection]);

    //Reset the dirty states of the fields after a save.
    useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    useEffect(() => {
      const tableDataChanged = !_isEqual(rowData, prevRowDataRef.current);
      setTableIsDirty(tableDataChanged);
    }, [rowData]);

    useEffect(() => {
      // Load tables from database
      let propertyowners = requestAPIData?.request?.contacts?.filter(
        (contact) => contact.contactType === ContactTypes.PropertyOwner
      );
      // If country is null, default to US
      propertyowners = propertyowners?.map((contact) =>
        contact.country === null ? { ...contact, country: 'US' } : contact
      );
      propertyowners && setRowData(propertyowners);
      prevRowDataRef.current = propertyowners;
      isTouched && trigger();
    }, [requestAPIData, isTouched, trigger]);

    useEffect(() => {
      if (isTouched) {
        if (hasAddPropertyOwners === 'true' && rowData?.length < 1) {
          setError('propertyOwnersTable', {
            type: 'custom',
            message: 'Property Owners: At least one property owner is required',
          });
        } else if (rowErrorIndices.length > 0) {
          setError('propertyOwnersTable', {
            type: 'manual',
            message: 'Property Owners: Table contains invalid values.',
          });
        } else {
          trigger('propertyOwnersTable');
        }
      }
    }, [hasAddPropertyOwners, rowData, rowErrorIndices, isTouched, setError, trigger]);

    useEffect(() => {
      const valid = hasAddPropertyOwners === 'false' ? true : hasAddPropertyOwners === 'true' && rowData?.length > 0;
      const validity = errorCount === 0 && valid;
      updateSection(stepNo, { valid: validity });
    }, [errorCount, rowData, hasAddPropertyOwners, updateSection, stepNo, isReadOnly]);

    useEffect(() => {
      if (rowData) {
        saveContactsTable('propOwners', rowData);
        const requiredFields = {
          firstname: true,
          lastname: true,
          address: true,
          country: true,
          city: true,
          state: true,
          zipcode: 'us',
          phoneonetype: true,
          phoneone: true,
          phoneonecountrycode: true,
        };
        const { errorIndices } = validateCSV(rowData, requiredFields);

        errorIndices && setRowErrorIndices(errorIndices);
      }
    }, [rowData, saveContactsTable]);

    useEffect(() => {
      doUpdatePreAppRequest({ hasAddPropertyOwners: hasAddPropertyOwners });
    }, [hasAddPropertyOwners, doUpdatePreAppRequest]);

    const handleAddMultiplePropertyOwnerRows = useCallback(
      (rows, fileName) => {
        const contactsHeaderMap = {
          salutation: 'salutation',
          'first name': 'firstName',
          'middle name': 'middleName',
          'last name': 'lastName',
          address: 'address',
          'address line 2': 'addressTwo',
          city: 'city',
          state: 'state',
          country: 'country',
          'zip code': 'zipcode',
          email: 'emailAddress',
          county: 'county',
          'phone one': 'phoneOne',
          'phone one country code': 'phoneOneCountryCode',
          'phone one type': 'phoneOneType',
          'phone one extension': 'phoneOneExtension',
          'phone two': 'phoneTwo',
          'phone two country code': 'phoneTwoCountryCode',
          'phone two extension': 'phoneTwoExtension',
          'phone two type': 'phoneTwoType',
          'fax country code': 'faxCountryCode',
          'fax phone': 'faxPhone',
          company: 'company',
          parcels: 'parcels',
        };

        const formatValue = (key, value) => {
          if (key === 'parcels' && !value) return [];
          if (!value) return '';
          switch (key) {
            case 'salutation': {
              return formatOptionString(value, salutations, false);
            }
            case 'state': {
              return formatState(value);
            }
            case 'country': {
              return formatCountry(value);
            }
            case 'phone one type':
            case 'phone two type': {
              return formatOptionString(value, phonetypes, false);
            }
            case 'parcels': {
              return value?.split(',')?.map((parcel) => ({ parcelNumber: parcel.trim() }));
            }
            case 'phone one country code':
            case 'phone two country code':
            case 'fax country code':
            case 'zip code': {
              return String(value);
            }
            default: {
              return value;
            }
          }
        };

        const requiredFields = {
          'first name': true,
          'last name': true,
          address: true,
          country: true,
          city: true,
          state: true,
          'zip code': 'us',
          'phone one type': true,
          'phone one': true,
          'phone one country code': true,
        };
        const { errors: rowErrors, errorIndices } = validateCSV(rows, requiredFields);

        errorIndices.length > 0 &&
          openSecondaryModal(statusModal, {
            status: 'Import contains missing or invalid data',
            msg: <CsvValidationModal errors={rowErrors} fileName={fileName} />,
          });

        const mappedRows = rows.map((row) => {
          return Object.fromEntries(
            Object.entries(row).map(([key, value]) => {
              return [contactsHeaderMap[key.toLowerCase()] || key.toLowerCase(), formatValue(key.toLowerCase(), value)];
            })
          );
        });

        if (mappedRows.length === 0) {
          return false;
        }

        const mappedRowsWithContactType = mappedRows.map((row) => ({
          ...row,
          contactType: ContactTypes.PropertyOwner,
        }));

        setRowData((oldData) => {
          const newRows = [...oldData, ...mappedRowsWithContactType];
          return newRows;
        });
      },
      [openSecondaryModal]
    );

    const handleFileInput = useCallback(
      async (e) => {
        if (!e.target.files || e.target.files.length === 0) return;
        await handleFileProcessing(async () => {
          const toastId = toast.loading('Processing file(s)...');
          const filesArr = Array.from(e.target.files) ?? [];
          const fileName = e.target.files[0].name;
          setStagedFile(filesArr);
          try {
            const flattenedData = await parseCsvWithXlsx(filesArr);
            const headers = flattenedData[0];
            const tableColumnHeaders = columnDefs?.map((column) => column.header);
            const additionalHeaders = [
              'address line 2',
              'salutation',
              'zip code',
              'email',
              'county',
              'phone one type',
              'phone one country code',
              'phone one',
              'phone one extension',
              'phone two type',
              'phone two country code',
              'phone two',
              'phone two extension',
              'fax country code',
              'fax phone',
              'company',
              'parcels',
            ];
            const columnHeaders = [...tableColumnHeaders, ...additionalHeaders];
            const headersAreValid = await validateCsvHeaders(headers, columnHeaders);
            if (!headersAreValid) {
              openSecondaryModal(statusModal, {
                status: 'Invalid Headers',
                msg: ErrorMessages.InvalidHeaders,
              });
              toast.dismiss(toastId);
              return;
            }
            const csvData = flattenedData.slice(1); // remove headers from flattened data
            const formattedRows = csvData.map((arr) => Object.fromEntries(headers.map((key, i) => [key, arr[i]])));
            const result = handleAddMultiplePropertyOwnerRows(formattedRows, fileName);
            if (result === false) {
              openSecondaryModal(statusModal, {
                status: 'No Valid Records',
                msg: 'No valid Property Owner records found in file.',
              });
              toast.dismiss(toastId);
              return;
            }
            tSuccess(toastId, 'File(s) processed successfully.');
          } catch (error) {
            console.error(error);
            tError(toastId, 'An error occurred while processing file(s).');
          } finally {
            setStagedFile([]); //clear the file input to allow another file to be uploaded
          }
        });
      },
      [handleAddMultiplePropertyOwnerRows, openSecondaryModal, columnDefs]
    );

    const handleAddClick = () =>
      openModal(AddPropertyOwnerModal, {
        setRowData,
        rowData,
        isReadOnly,
      });

    return (
      <FormProvider {...methods}>
        {errors && isTouched && !isReadOnly && <ErrorSummary errors={errors} sectionNo={stepNo} />}
        <StepHeader
          text='Property Owners'
          subtext='If the USACE determines a site visit is needed, you must include contact information for all current deeded property owners and provide a signed right-of-entry for each owner. A right-of-entry form allows the USACE to access the project area when conducting a site investigation or jurisdictional determination. This form ensures that the USACE has the legal right to enter the property.'
        >
          <p>
            <LinkButton
              onClick={() => doDownloadFile(ResourceFiles.Eng6294, FileTypes.Resource)}
              title='Download a blank Right of Entry form'
              content='Download a blank Right of Entry form'
            />{' '}
            to complete or{' '}
            <LinkButton
              onClick={() => doDownloadPopulatedFile(ContactsFormNames.RightOfEntry, ResourceFiles.Eng6294)}
              title='download a Right of Entry form populated'
              content='download a Right of Entry form populated'
            />{' '}
            with the information you entered. and upload the completed document in the section below.
          </p>
          <p>
            Property owners can be provided in this section by adding them individually or bulk loading them using the
            provided bulk upload CSV.
          </p>
          <div className='margin-top-1 margin-bottom-2'>
            Download CSV Bulk Upload Template:
            <ul>
              <li>
                <LinkButton
                  onClick={() => doDownloadFile(ResourceFiles.PropertyOwnerBulkUploadCSV, FileTypes.Resource)}
                  title='Property Owner Bulk Upload CSV Template [CSV, 2 KB]'
                  content='Property Owner Bulk Upload CSV Template [CSV, 2 KB]'
                />
              </li>
              <ul>
                <li>Use for uploading property owners</li>
              </ul>
            </ul>
          </div>
        </StepHeader>

        <RightOfEntryUpload
          componentID={componentID}
          subText={
            <>
              <p>
                The property owner or an easement holder must provide right-of-entry to the U.S. Army Corps of Engineers
                and be a duly authorized owner of record of the property.
              </p>
              <p>
                If the agent signs the right-of-entry, they must have an agent authorization signed by the property
                owner.
              </p>
            </>
          }
        />
        <Section>
          <div id='propertyOwnersTable'>
            <SelectInput
              className='width-50'
              fullWidth={false}
              name='hasAddPropertyOwners'
              label='Would you like to request an onsite pre-application meeting and are there any property owners other than the applicant?'
              required
              readOnly={isReadOnly}
            >
              <option key='1' value='true'>
                Yes
              </option>
              <option key='2' value='false'>
                No
              </option>
            </SelectInput>
          </div>

          {hasAddPropertyOwners === 'true' && (
            <>
              <div className='margin-top-4'>
                <FileInput
                  accept='.xlsx,.txt,.csv'
                  id={`property-owner-bulk-file`}
                  name={`property-owner-bulk-file`}
                  dragText='Drag .csv or .xlsx file here or '
                  aria-label='Upload CSV or XLSX file'
                  onChangeCallback={handleFileInput}
                  arHeader={true}
                  value={stagedFile}
                  readOnly={isReadOnly}
                />
              </div>
              <div className='table-button-container'>
                {rowData.length > 0 && (
                  <Button
                    className='add-property-owner-button'
                    title='Add New Contact'
                    size='small'
                    onClick={() =>
                      openModal(AddPropertyOwnerModal, {
                        setRowData: setRowData,
                        rowData: rowData,
                        isReadOnly: isReadOnly,
                      })
                    }
                    disabled={isReadOnly}
                  >
                    <Icon focusable={false} path={mdiPlusBox} size={'16px'} />
                    Add New Contact
                  </Button>
                )}
                {rowData.length !== 0 && (
                  <Button
                    className={`clear-table-button ${rowData.length === 0 || isReadOnly ? 'disabled' : 'hover'}`}
                    title='Remove All Contacts'
                    size='small'
                    onClick={() => {
                      setRowData([]);
                      doClearContactType(ContactTypes.PropertyOwner);
                    }}
                    disabled={isReadOnly || rowData.length < 1}
                  >
                    <Icon focusable={false} path={mdiCloseOctagon} size={'16px'} />
                    Remove All Contacts
                  </Button>
                )}
              </div>
              <div className='width-full margin-bottom-2'>
                <TanStackTableBasic
                  data={rowData}
                  columns={columnDefs}
                  placeholderText='No property owners found.'
                  addButtonText='Add New Contact'
                  onAddClick={handleAddClick}
                  isReadOnly={isReadOnly}
                  rowErrorIndices={rowErrorIndices}
                />
              </div>
            </>
          )}
        </Section>
      </FormProvider>
    );
  }
);

PreAppFormPropertyOwners.metadata = PreAppFormPropertyOwnersMetadata;

export default PreAppFormPropertyOwners;
